import type { MouseEvent, MouseEventHandler, PropsWithChildren } from 'react';
import { useCallback } from 'react';
import { noop } from 'lodash';
import Link from 'next/link';
import styled from 'styled-components';
import { Box, isMobile, theme, whenHovered } from '@frameio/vapor';

export type ResultItemProps = {
  href: string;
  isSelected?: boolean;
  onClick?: MouseEventHandler;
  onDoubleClick?: () => void;
};

export function ResultItemCell({
  children,
  isSelected = false,
  onClick = noop,
  onDoubleClick = noop,
  href,
}: PropsWithChildren<ResultItemProps>) {
  const handleClick = useCallback(
    (e: MouseEvent) => {
      if (!e.ctrlKey && !e.metaKey) {
        e.preventDefault();
      }
      onClick?.(e);
    },
    [onClick]
  );

  /**
   *
   * ASSETS-215 has removed search term highlighting in the results.
   *
   * \w is a short hand for matching A-Za-z0-9, so essentially we are
   * matching only those characters and ignoring any special characters
   * for highlighting. Currently this is how the backend is searching, so
   * we're keeping it consistent.
   *
   * const searchWords = searchTerm.match(/([\w']+)/g);
   */
  return (
    <Link
      href={href}
      onClick={isMobile ? onDoubleClick : handleClick}
      onDoubleClick={isMobile ? noop : onDoubleClick}
    >
      <ResultContainer
        $isSelected={isSelected}
        data-testid="quick-search-result-cell"
      >
        <Box
          alignItems="center"
          display="flex"
          flexDirection="row"
          gap={12}
          justifyContent="flex-start"
          style={{ height: '100%' }}
        >
          {children}
        </Box>
      </ResultContainer>
    </Link>
  );
}

const ResultContainer = styled.div<{
  $isSelected: boolean;
}>`
  background-color: transparent;
  color: ${theme.colors.icon.primary};
  height: ${theme.spaces[48]};
  padding: ${theme.spaces[6]} ${theme.spaces[16]};
  /* ensures that the icon gets this color  */
  width: 100%;
  ${whenHovered} {
    background: ${theme.colors.background.interactive.hover};
  }

  ${theme.mediaQueries.tablet} {
    background-color: ${({ $isSelected }) =>
      $isSelected
        ? theme.colors.background.interactive.selected
        : 'transparent'};

    ${whenHovered} {
      background: ${({ $isSelected }) =>
        $isSelected
          ? theme.colors.background.interactive.selected
          : theme.colors.background.interactive.hover};
    }
  }
`;
